import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useBusiness } from '../contexts/BusinessContext';
import { useAuth } from '../contexts/AuthContext';
import AdminApiService from '../services/api.service';
import UsersPage from './UsersPage';
import SettingsPage from './SettingsPage';
import { 
  PieChart, 
  Pie, 
  Cell, 
  BarChart, 
  Bar, 
  LineChart, 
  Line,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';

// Menu management icons
const SearchIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
  </svg>
);

const AddIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
  </svg>
);

const EditIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
  </svg>
);


const FilterIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
  </svg>
);

const DragIndicatorIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
  </svg>
);

const DeleteIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
  </svg>
);

const ImageIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
  </svg>
);

// Icons (replacing Material UI icons)
const RestaurantIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M6 3.75A2.75 2.75 0 018.75 1h2.5A2.75 2.75 0 0114 3.75v.443c.572.055 1.14.122 1.706.2C17.053 4.582 18 5.75 18 7.07v3.469c0 1.126-.694 2.191-1.83 2.54-1.952.599-4.024.921-6.17.921s-4.219-.322-6.17-.921C2.694 12.73 2 11.665 2 10.539V7.07c0-1.321.947-2.489 2.294-2.676A41.047 41.047 0 016 4.193V3.75zm6.5 0v.325a41.622 41.622 0 00-5 0V3.75c0-.69.56-1.25 1.25-1.25h2.5c.69 0 1.25.56 1.25 1.25zM10 10a1 1 0 00-1 1v.01a1 1 0 001 1h.01a1 1 0 001-1V11a1 1 0 00-1-1H10z" clipRule="evenodd" />
    <path d="M3 15.055v-.684c.126.053.255.1.39.142 2.092.642 4.313.987 6.61.987 2.297 0 4.518-.345 6.61-.987.135-.041.264-.089.39-.142v.684c0 1.347-.985 2.53-2.363 2.686a41.454 41.454 0 01-9.274 0C3.985 17.585 3 16.402 3 15.055z" />
  </svg>
);

const PhoneIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
  </svg>
);

const EmailIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
  </svg>
);

const LocationIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
  </svg>
);

const LanguageIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0 0116 10c0 .34-.028.675-.083 1H15a2 2 0 00-2 2v2.197A5.973 5.973 0 0110 16v-2a2 2 0 00-2-2 2 2 0 01-2-2 2 2 0 00-1.668-1.973z" clipRule="evenodd" />
  </svg>
);

const PersonIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
  </svg>
);

const MenuIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
  </svg>
);

const SettingsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
  </svg>
);

const ArrowBackIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
  </svg>
);


// No mock data - using real API data only

// Sample colors for charts
const COLORS = ['#00C6FF', '#FF5722', '#FFBB28', '#FF8042', '#8884d8'];

const BusinessDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { selectedBusiness, selectBusiness } = useBusiness();
  const { isPlatformAdmin } = useAuth();
  // Check if we have a tab value in localStorage (set by AdminLayout navigation)
  const initialTabValue = (() => {
    const storedValue = localStorage.getItem('businessDetailTabValue');
    if (storedValue) {
      // Clear the value from localStorage after reading it
      localStorage.removeItem('businessDetailTabValue');
      return parseInt(storedValue, 10);
    }
    return 0;
  })();
  
  const [tabValue, setTabValue] = useState(initialTabValue);
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [timeframe, setTimeframe] = useState('week');
  
  // Menu management state
  const [menuLoading, setMenuLoading] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [menuActiveTab, setMenuActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [openItemDialog, setOpenItemDialog] = useState(false);
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [newItem, setNewItem] = useState({
    name: '',
    description: '',
    price: '',
    categoryId: '',
    available: true,
    tags: []
  });
  const [newCategory, setNewCategory] = useState({
    name: ''
  });
  
  // Fetch business data
  useEffect(() => {
    // Use a ref to track if we've attempted to fetch this business already
    const fetchAttempted = { current: false };
    
    const fetchBusinessData = async () => {
      // If we've already tried fetching this business, don't try again automatically
      if (fetchAttempted.current) {
        console.log(`Skipping automatic business fetch for ID ${id} - already attempted`);
        return;
      }
      
      fetchAttempted.current = true;
      setLoading(true);
      
      try {
        // First check if the business is already in the context and matches the ID
        if (selectedBusiness && selectedBusiness.id === id) {
          console.log("Using selected business from context:", selectedBusiness);
          setBusiness(selectedBusiness);
        } else {
          // Try to fetch the business directly using the API service
          try {
            console.log(`Fetching business details for ID: ${id}`);
            const businessData = await AdminApiService.businesses.getById(id);
            console.log("Retrieved business data:", businessData);
            
            if (businessData) {
              setBusiness(businessData);
              
              // Also update the business context
              if (!selectedBusiness || selectedBusiness.id !== id) {
                // Only attempt to update context once
                const contextUpdateAttempted = { current: false };
                
                if (!contextUpdateAttempted.current) {
                  contextUpdateAttempted.current = true;
                  
                  selectBusiness(id).catch(err => {
                    console.warn("Could not update business context:", err);
                  });
                }
              }
            } else {
              console.warn("No business data returned from API");
              setBusiness(null);
            }
          } catch (error) {
            console.error("Failed to fetch business from API:", error);
            setBusiness(null);
          }
        }
      } catch (error) {
        console.error("Error in business data fetch process:", error);
        setBusiness(null);
      } finally {
        setLoading(false);
      }
    };
    
    fetchBusinessData();
  }, [id, selectedBusiness, selectBusiness]);
  
  // Fetch menu data
  const fetchMenuData = useCallback(async () => {
    if (!business || !business.id) return;
    
    setMenuLoading(true);
    try {
      // Get menu data from API - includes both items and categories
      const menuData = await AdminApiService.menu.getAll(business.id);
      console.log('Menu data:', menuData);
      
      if (menuData) {
        setMenuItems(menuData.items || []);
        setCategories(menuData.categories || []);
      } else {
        setMenuItems([]);
        setCategories([]);
      }
    } catch (error) {
      console.error('Failed to fetch menu data:', error);
      // Set empty arrays to prevent UI errors
      setMenuItems([]);
      setCategories([]);
    } finally {
      setMenuLoading(false);
    }
  }, [business]);
  
  // Load menu data when the component mounts if menu tab is active
  useEffect(() => {
    if (business && tabValue === 6) {
      fetchMenuData();
    }
  }, [business, tabValue, fetchMenuData]);
  
  // Handle tab change
  const handleTabChange = (newValue) => {
    setTabValue(newValue);
    
    // Load dashboard data when switching to dashboard tab
    if (newValue === 1 && !dashboardData && business) {
      fetchDashboardData();
    }
    
    // Load menu data when switching to menu tab
    if (newValue === 6 && business) {
      fetchMenuData();
    }
  };
  
  // Function to fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    if (!business || !business.id) return;
    
    try {
      setDashboardLoading(true);
      
      // Use the analytics endpoint to get dashboard data
      const data = await AdminApiService.analytics.getDashboardStats(business.id, timeframe);
      setDashboardData(data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setDashboardLoading(false);
    }
  }, [business, timeframe]);
  
  // Handle timeframe change
  const handleTimeframeChange = (event) => {
    setTimeframe(event.target.value);
    // Refetch dashboard data with new timeframe
    if (tabValue === 1) {
      fetchDashboardData();
    }
  };
  
  // Menu tab handlers
  const handleMenuTabChange = (newValue) => {
    setMenuActiveTab(newValue);
  };
  
  // Status Modal state
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  
  // Handle business activation/deactivation
  const toggleBusinessStatus = async () => {
    try {
      // Determine the new status based on current status
      const newStatus = business.status === 'active' ? 'suspended' : 'active';
      
      // Call API to update status
      const result = await AdminApiService.businesses.updateStatus(business.id, newStatus);
      
      if (result) {
        // Update business object with new status
        setBusiness(prev => ({
          ...prev,
          status: newStatus
        }));
        
        // Close the modal
        setStatusModalOpen(false);
      }
    } catch (error) {
      console.error('Failed to update business status:', error);
      alert('Failed to update business status. Please try again.');
    }
  };
  
  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  // Filter menu open/close
  const toggleFilterMenu = () => {
    setFilterMenuOpen(!filterMenuOpen);
  };
  
  // Handle category selection for filtering
  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
    setFilterMenuOpen(false);
  };
  
  // Item dialog open/close
  const handleItemDialogOpen = () => {
    setOpenItemDialog(true);
  };
  
  const handleItemDialogClose = () => {
    setOpenItemDialog(false);
    setNewItem({
      name: '',
      description: '',
      price: '',
      categoryId: '',
      available: true,
      tags: []
    });
  };
  
  // Category dialog open/close
  const handleCategoryDialogOpen = () => {
    setOpenCategoryDialog(true);
  };
  
  const handleCategoryDialogClose = () => {
    setOpenCategoryDialog(false);
    setNewCategory({ name: '' });
  };
  
  // Handle new item form change
  const handleNewItemChange = (e) => {
    const { name, value, checked, type } = e.target;
    setNewItem(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  // Handle new category form change
  const handleNewCategoryChange = (e) => {
    const { name, value } = e.target;
    setNewCategory(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Submit new item
  const handleItemSubmit = async () => {
    try {
      // Validate input
      if (!newItem.name || !newItem.price || !newItem.categoryId) {
        alert('Please fill in all required fields');
        return;
      }
      
      const itemData = {
        ...newItem,
        // Convert price to a number
        price: parseFloat(newItem.price)
      };
      
      // Call API to create item
      await AdminApiService.menu.create(business.id, itemData);
      
      // Refresh menu data
      fetchMenuData();
      
      // Close dialog
      handleItemDialogClose();
    } catch (error) {
      console.error('Failed to create menu item:', error);
      alert('Failed to create menu item. Please try again.');
    }
  };
  
  // Submit new category
  const handleCategorySubmit = async () => {
    try {
      // Validate input
      if (!newCategory.name) {
        alert('Please enter a category name');
        return;
      }
      
      // Call API to create category
      await AdminApiService.menu.createCategory(business.id, newCategory);
      
      // Refresh menu data
      fetchMenuData();
      
      // Close dialog
      handleCategoryDialogClose();
    } catch (error) {
      console.error('Failed to create category:', error);
      alert('Failed to create category. Please try again.');
    }
  };
  
  // Toggle item availability
  const toggleItemAvailability = async (itemId) => {
    try {
      // Find the item
      const item = menuItems.find(item => item.id === itemId);
      if (!item) return;
      
      // Update item in UI optimistically
      setMenuItems(prev => 
        prev.map(item => 
          item.id === itemId ? { ...item, available: !item.available } : item
        )
      );
      
      // Call API to update item
      await AdminApiService.menu.update(business.id, itemId, {
        ...item,
        available: !item.available
      });
    } catch (error) {
      console.error('Failed to toggle item availability:', error);
      
      // Revert UI change on error
      fetchMenuData();
      alert('Failed to update item. Please try again.');
    }
  };
  
  // Delete menu item
  const handleDeleteMenuItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this menu item?')) {
      return;
    }
    
    try {
      // Call API to delete the item
      await AdminApiService.menu.delete(business.id, itemId);
      
      // Update UI optimistically
      setMenuItems(prev => prev.filter(item => item.id !== itemId));
    } catch (error) {
      console.error('Failed to delete menu item:', error);
      
      // Revert UI change on error
      fetchMenuData();
      alert('Failed to delete menu item. Please try again.');
    }
  };
  
  // Delete category
  const handleDeleteCategory = async (categoryId) => {
    // Check if there are any menu items using this category
    const itemsInCategory = menuItems.filter(item => item.categoryId === categoryId);
    
    if (itemsInCategory.length > 0) {
      alert(`Cannot delete this category because it contains ${itemsInCategory.length} menu items. Please move or delete these items first.`);
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this category?')) {
      return;
    }
    
    try {
      // Call API to delete the category
      await AdminApiService.menu.deleteCategory(business.id, categoryId);
      
      // Update UI optimistically
      setCategories(prev => prev.filter(cat => cat.id !== categoryId));
    } catch (error) {
      console.error('Failed to delete category:', error);
      
      // Revert UI change on error
      fetchMenuData();
      alert('Failed to delete category. Please try again.');
    }
  };
  
  // Get category name by ID
  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : 'Uncategorized';
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-[50vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }
  
  if (!business) {
    return (
      <div className="text-center py-12">
        <div className="flex justify-center mb-4">
          <RestaurantIcon />
        </div>
        <h2 className="text-xl font-semibold mb-2">
          Business Not Found
        </h2>
        <p className="text-gray-400 mb-6">
          The business you're looking for doesn't exist or you don't have access to it.
        </p>
        <Link 
          to="/businesses"
          className="btn-primary inline-flex items-center"
        >
          <ArrowBackIcon />
          <span className="ml-2">Back to Businesses</span>
        </Link>
      </div>
    );
  }
  
  // Format date
  const formatDate = (dateString) => {
    try {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid date';
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return 'Date error';
    }
  };
  
  // Format currency
  const formatCurrency = (amount) => {
    try {
      if (amount === undefined || amount === null) return '$0.00';
      const numAmount = Number(amount);
      if (isNaN(numAmount)) return '$0.00';
      
      return `$${numAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    } catch (error) {
      console.error("Error formatting currency:", error);
      return '$0.00';
    }
  };
  
  return (
    <div>
      <div className="mb-8">
        <Link
          to="/businesses"
          className="inline-flex items-center text-gray-400 hover:text-white mb-4"
        >
          <ArrowBackIcon />
          <span className="ml-2">Back to Businesses</span>
        </Link>
        
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <h1 className="text-2xl font-bold text-white">
              {business.name}
            </h1>
            <span 
              className={`ml-4 px-2 py-1 text-xs font-medium rounded-full ${
                business.status === 'active' 
                  ? 'bg-green-900 text-green-300'
                  : 'bg-gray-700 text-gray-300'
              }`}
            >
              {business.status === 'active' ? 'Active' : 'Inactive'}
            </span>
          </div>
          
        </div>
      </div>
      
      <div className="border-b border-dark-600 mb-6">
        <div className="flex flex-wrap">
          <button
            className={`px-4 py-2 font-medium text-sm focus:outline-none ${
              tabValue === 0 
                ? 'text-primary-500 border-b-2 border-primary-500' 
                : 'text-gray-400 hover:text-gray-300'
            }`}
            onClick={() => handleTabChange(0)}
          >
            Overview
          </button>
          <button
            className={`px-4 py-2 font-medium text-sm focus:outline-none ${
              tabValue === 1 
                ? 'text-primary-500 border-b-2 border-primary-500' 
                : 'text-gray-400 hover:text-gray-300'
            }`}
            onClick={() => handleTabChange(1)}
          >
            Dashboard
          </button>
          <button
            className={`px-4 py-2 font-medium text-sm focus:outline-none ${
              tabValue === 2 
                ? 'text-primary-500 border-b-2 border-primary-500' 
                : 'text-gray-400 hover:text-gray-300'
            }`}
            onClick={() => handleTabChange(2)}
          >
            Orders
          </button>
          <button
            className={`px-4 py-2 font-medium text-sm focus:outline-none ${
              tabValue === 3 
                ? 'text-primary-500 border-b-2 border-primary-500' 
                : 'text-gray-400 hover:text-gray-300'
            }`}
            onClick={() => handleTabChange(3)}
          >
            Customers
          </button>
          <button
            className={`px-4 py-2 font-medium text-sm focus:outline-none ${
              tabValue === 6 
                ? 'text-primary-500 border-b-2 border-primary-500' 
                : 'text-gray-400 hover:text-gray-300'
            }`}
            onClick={() => handleTabChange(6)}
          >
            Menu
          </button>
          <button
            className={`px-4 py-2 font-medium text-sm focus:outline-none ${
              tabValue === 7 
                ? 'text-primary-500 border-b-2 border-primary-500' 
                : 'text-gray-400 hover:text-gray-300'
            }`}
            onClick={() => handleTabChange(7)}
          >
            Users
          </button>
          <button
            className={`px-4 py-2 font-medium text-sm focus:outline-none ${
              tabValue === 4 
                ? 'text-primary-500 border-b-2 border-primary-500' 
                : 'text-gray-400 hover:text-gray-300'
            }`}
            onClick={() => handleTabChange(4)}
          >
            Integrations
          </button>
          <button
            className={`px-4 py-2 font-medium text-sm focus:outline-none ${
              tabValue === 5 
                ? 'text-primary-500 border-b-2 border-primary-500' 
                : 'text-gray-400 hover:text-gray-300'
            }`}
            onClick={() => handleTabChange(5)}
          >
            Settings
          </button>
        </div>
      </div>
      
      {/* Overview Tab */}
      {tabValue === 0 && (
        <div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {/* Business Information */}
            <div className="md:col-span-4">
              <div className="card">
                <div className="p-4 border-b border-dark-600">
                  <h2 className="text-lg font-medium">Business Information</h2>
                </div>
                
                <div className="p-4">
                  <ul className="space-y-4">
                    <li className="flex">
                      <div className="w-10 text-gray-400">
                        <RestaurantIcon />
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Type</p>
                        <p className="capitalize">{business.type || 'restaurant'}</p>
                      </div>
                    </li>
                    
                    <li className="flex">
                      <div className="w-10 text-gray-400">
                        <PhoneIcon />
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Phone</p>
                        <p>{business.phone || 'Not set'}</p>
                      </div>
                    </li>
                    
                    <li className="flex">
                      <div className="w-10 text-gray-400">
                        <EmailIcon />
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Email</p>
                        <p>{business.email || business.contactEmail || 'Not set'}</p>
                      </div>
                    </li>
                    
                    <li className="flex">
                      <div className="w-10 text-gray-400">
                        <LocationIcon />
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Address</p>
                        <p>{business.address || 'Not set'}</p>
                      </div>
                    </li>
                    
                    <li className="flex">
                      <div className="w-10 text-gray-400">
                        <LanguageIcon />
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Website</p>
                        <p>{business.website || 'Not set'}</p>
                      </div>
                    </li>
                    
                    <li className="flex">
                      <div className="w-10 text-gray-400">
                        <LanguageIcon />
                      </div>
                      <div>
                        <p className="text-gray-400 text-sm">Virtual Menu URL</p>
                        <p>{business.subdomain ? `${business.subdomain}.satvada.co.uk` : 'Subdomain not set'}</p>
                        {business.custom_domain && (
                          <>
                            <p className="text-gray-400 text-sm mt-2">Custom Domain</p>
                            <p>{business.custom_domain}</p>
                          </>
                        )}
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="card mt-6">
                <div className="p-4 border-b border-dark-600">
                  <h2 className="text-lg font-medium">Business Owners</h2>
                </div>
                
                <div className="p-4">
                  <ul className="space-y-4">
                    {business.owners && business.owners.length > 0 ? (
                      business.owners.map((owner, index) => (
                        <li key={index} className="flex justify-between items-center">
                          <div className="flex">
                            <div className="w-10 text-gray-400">
                              <PersonIcon />
                            </div>
                            <span>{owner}</span>
                          </div>
                          <button className="text-gray-400 hover:text-primary-400">
                            <EditIcon />
                          </button>
                        </li>
                      ))
                    ) : (
                      <li className="text-gray-400">No owners listed</li>
                    )}
                  </ul>
                  
                  <button 
                    className="mt-4 inline-flex items-center text-primary-400 hover:text-primary-300 text-sm"
                  >
                    <PersonIcon />
                    <span className="ml-2">Add Owner</span>
                  </button>
                </div>
              </div>
            </div>
            
            {/* Business Statistics */}
            <div className="md:col-span-8">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
                {/* Key Metrics */}
                <div className="card p-4 text-center">
                  <p className="text-4xl font-bold text-primary-500">
                    {business.orders || 0}
                  </p>
                  <p className="text-gray-400">
                    Total Orders
                  </p>
                </div>
                
                <div className="card p-4 text-center">
                  <p className="text-4xl font-bold text-primary-500">
                    {business.locations || 1}
                  </p>
                  <p className="text-gray-400">
                    Locations
                  </p>
                </div>
                
                <div className="card p-4 text-center">
                  <p className="text-4xl font-bold text-primary-500">
                    {business.revenue && business.revenue.total 
                      ? formatCurrency(business.revenue.total) 
                      : '$0.00'}
                  </p>
                  <p className="text-gray-400">
                    Total Revenue
                  </p>
                </div>
              </div>
              
              <div className="card mb-6">
                <div className="p-4 border-b border-dark-600">
                  <h2 className="text-lg font-medium">Business Overview</h2>
                </div>
                
                <div className="p-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <div className="mb-4">
                        <p className="text-sm text-gray-400">
                          Created On
                        </p>
                        <p>
                          {business.createdAt ? formatDate(business.createdAt) : 'Recently created'}
                        </p>
                      </div>
                      
                      <div className="mb-4">
                        <p className="text-sm text-gray-400">
                          Last Order
                        </p>
                        <p>
                          {business.lastOrderAt ? formatDate(business.lastOrderAt) : 'No orders yet'}
                        </p>
                      </div>
                    </div>
                    
                    <div>
                      <div className="mb-4">
                        <p className="text-sm text-gray-400">
                          Last Month Revenue
                        </p>
                        <p>
                          {business.revenue && business.revenue.lastMonth 
                            ? formatCurrency(business.revenue.lastMonth) 
                            : '$0.00'}
                        </p>
                      </div>
                      
                      <div className="mb-4">
                        <p className="text-sm text-gray-400">
                          Subdomain
                        </p>
                        <p>
                          {business.subdomain || 'Not set'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="card">
                <div className="p-4">
                  <button
                    onClick={() => setStatusModalOpen(true)}
                    className={`w-full inline-flex justify-center items-center px-4 py-2 rounded-md font-medium ${
                      business.status === 'active'
                        ? 'border border-red-700 bg-dark-700 text-red-500 hover:bg-red-900 hover:bg-opacity-20'
                        : 'border border-green-700 bg-dark-700 text-green-500 hover:bg-green-900 hover:bg-opacity-20'
                    } transition-colors duration-200`}
                  >
                    {business.status === 'active' ? <DeleteIcon /> : <CheckCircleIcon />}
                    <span className="ml-2">
                      {business.status === 'active' ? 'Deactivate' : 'Activate'}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Dashboard Tab */}
      {tabValue === 1 && (
        <div>
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-2xl font-bold text-white">
              {business.name} Dashboard
            </h1>
            
            <div className="relative">
              <select
                id="timeframe-select"
                value={timeframe}
                onChange={handleTimeframeChange}
                className="form-input pl-3 pr-10 py-2 text-sm w-40"
              >
                <option value="day">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="year">This Year</option>
              </select>
            </div>
          </div>
          
          {dashboardLoading ? (
            <div className="flex justify-center items-center h-[300px]">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
            </div>
          ) : (
            <div>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <div className="card p-4">
                  <p className="text-gray-400 text-sm mb-1">
                    Total Orders
                  </p>
                  <p className="text-2xl font-semibold">
                    {dashboardData?.recentOrders?.count || 0}
                  </p>
                  <p className="text-sm text-gray-400">
                    {dashboardData?.recentOrders?.trend 
                      ? `${dashboardData.recentOrders.trend} vs previous period` 
                      : 'No previous data for comparison'}
                  </p>
                </div>
                
                <div className="card p-4">
                  <p className="text-gray-400 text-sm mb-1">
                    Revenue
                  </p>
                  <p className="text-2xl font-semibold">
                    ${dashboardData?.revenue?.total || 0}
                  </p>
                  <p className="text-sm text-gray-400">
                    {dashboardData?.revenue?.trend 
                      ? `${dashboardData.revenue.trend} vs previous period` 
                      : 'No previous data for comparison'}
                  </p>
                </div>
                
                <div className="card p-4">
                  <p className="text-gray-400 text-sm mb-1">
                    Voice Orders
                  </p>
                  <p className="text-2xl font-semibold">
                    {dashboardData?.ordersByType?.voice || 0}
                  </p>
                  <p className="text-sm text-gray-400">
                    {dashboardData?.voiceOrdersTrend 
                      ? `${dashboardData.voiceOrdersTrend} vs previous period` 
                      : 'No previous data for comparison'}
                  </p>
                </div>
                
                <div className="card p-4">
                  <p className="text-gray-400 text-sm mb-1">
                    Customers
                  </p>
                  <p className="text-2xl font-semibold">
                    {dashboardData?.customers?.count || 0}
                  </p>
                  <p className="text-sm text-gray-400">
                    {dashboardData?.customers?.newCustomers 
                      ? `${dashboardData.customers.newCustomers} new customers` 
                      : 'No new customers'}
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
                <div className="lg:col-span-8">
                  <div className="card">
                    <div className="p-4 border-b border-dark-600">
                      <h3 className="text-lg font-medium">Orders Over Time</h3>
                    </div>
                    <div className="p-4">
                      {!dashboardData || !dashboardData.recentOrders?.data?.length ? (
                        <div className="h-[300px] flex flex-col justify-center items-center">
                          <div className="text-center text-gray-400 p-6 rounded-md">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto mb-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                            <p className="text-lg font-medium mb-2">No Orders Yet</p>
                            <p className="text-sm">Order history will appear here when orders are placed.</p>
                          </div>
                        </div>
                      ) : (
                        <div className="h-[300px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              data={dashboardData.recentOrders.data}
                              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#2D2D2D" />
                              <XAxis dataKey="name" stroke="#9CA3AF" />
                              <YAxis stroke="#9CA3AF" />
                              <Tooltip 
                                contentStyle={{ 
                                  backgroundColor: '#1E1E1E', 
                                  borderColor: '#2D2D2D',
                                  color: '#FFF'
                                }} 
                              />
                              <Legend />
                              <Bar dataKey="orders" fill="#00C6FF" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="lg:col-span-4">
                  <div className="card h-full">
                    <div className="p-4 border-b border-dark-600">
                      <h3 className="text-lg font-medium">Order Types</h3>
                    </div>
                    <div className="p-4">
                      {!dashboardData || !dashboardData.popularItems?.length ? (
                        <div className="h-[300px] flex flex-col justify-center items-center">
                          <div className="text-center text-gray-400 p-6 rounded-md">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto mb-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                            <p className="text-lg font-medium mb-2">No Data Yet</p>
                            <p className="text-sm">Order type distribution will appear here when orders are placed.</p>
                          </div>
                        </div>
                      ) : (
                        <div className="h-[300px] flex justify-center items-center">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={dashboardData.ordersByType || [
                                  { name: 'Voice', value: 0 },
                                  { name: 'Chat', value: 0 },
                                  { name: 'Web', value: 0 }
                                ]}
                                cx="50%"
                                cy="50%"
                                labelLine={true}
                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="value"
                              >
                                {(dashboardData.ordersByType || []).map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip 
                                contentStyle={{ 
                                  backgroundColor: '#1E1E1E', 
                                  borderColor: '#2D2D2D',
                                  color: '#FFF'
                                }}
                              />
                              <Legend />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="card">
                <div className="p-4 border-b border-dark-600">
                  <h3 className="text-lg font-medium">Popular Items</h3>
                </div>
                <div className="p-4">
                  {!dashboardData || !dashboardData.popularItems?.length ? (
                    <p className="text-sm text-gray-400">
                      No popular items data available yet. This section will display your most ordered menu items.
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full">
                        <thead>
                          <tr>
                            <th className="px-4 py-2 text-left text-sm font-medium text-gray-400">Item</th>
                            <th className="px-4 py-2 text-left text-sm font-medium text-gray-400">Orders</th>
                            <th className="px-4 py-2 text-left text-sm font-medium text-gray-400">Revenue</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-dark-600">
                          {dashboardData.popularItems.map((item, index) => (
                            <tr key={index}>
                              <td className="px-4 py-3 whitespace-nowrap">{item.name}</td>
                              <td className="px-4 py-3 whitespace-nowrap">{item.quantity}</td>
                              <td className="px-4 py-3 whitespace-nowrap">${item.revenue}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Orders Tab */}
      {tabValue === 2 && (
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold mb-2">
            Orders Management
          </h2>
          <p className="text-gray-400">
            This section is under construction. It will display order history and management.
          </p>
        </div>
      )}
      
      {/* Customers Tab */}
      {tabValue === 3 && (
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold mb-2">
            Customers Management
          </h2>
          <p className="text-gray-400">
            This section is under construction. It will display customer data and analytics.
          </p>
        </div>
      )}
      
      {/* Integrations Tab - Embedded integrations management */}
      {tabValue === 4 && (
        <div>
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4">
              Available Integrations
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Twilio Integration Card */}
              <div 
                className="card cursor-pointer hover:shadow-glow transition-all duration-200 overflow-hidden"
                style={{ borderTop: "4px solid #F22F46" }}
                onClick={() => {}}
              >
                <div className="p-5 flex flex-col h-full">
                  <div className="flex justify-between items-start mb-4">
                    <div style={{ color: "#F22F46" }}>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                      </svg>
                    </div>
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-900 text-green-300">
                      Active
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold mb-2">
                    Twilio
                  </h3>
                  <p className="text-gray-400 text-sm mb-4 flex-grow">
                    Voice and SMS integration for phone ordering
                  </p>
                  <div className="flex justify-end items-center text-primary-500">
                    <span className="text-sm">Configure</span>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-1" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              </div>
              
              {/* Toast POS Integration Card */}
              <div 
                className="card cursor-pointer hover:shadow-glow transition-all duration-200 overflow-hidden"
                style={{ borderTop: "4px solid #FF9E15" }}
                onClick={() => {}}
              >
                <div className="p-5 flex flex-col h-full">
                  <div className="flex justify-between items-start mb-4">
                    <div style={{ color: "#FF9E15" }}>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
                      </svg>
                    </div>
                    <span className="px-2 py-1 text-xs font-medium rounded-full border border-gray-500 text-gray-400">
                      Inactive
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold mb-2">
                    Toast POS
                  </h3>
                  <p className="text-gray-400 text-sm mb-4 flex-grow">
                    Point of sale integration for order management
                  </p>
                  <div className="flex justify-end items-center text-primary-500">
                    <span className="text-sm">Set Up</span>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-1" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              </div>
              
              {/* Stripe Integration Card */}
              <div 
                className="card cursor-pointer hover:shadow-glow transition-all duration-200 overflow-hidden"
                style={{ borderTop: "4px solid #6772E5" }}
                onClick={() => {}}
              >
                <div className="p-5 flex flex-col h-full">
                  <div className="flex justify-between items-start mb-4">
                    <div style={{ color: "#6772E5" }}>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                        <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-900 text-green-300">
                      Active
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold mb-2">
                    Stripe
                  </h3>
                  <p className="text-gray-400 text-sm mb-4 flex-grow">
                    Payment processing for online orders
                  </p>
                  <div className="flex justify-end items-center text-primary-500">
                    <span className="text-sm">Configure</span>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-1" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              </div>
              
              {/* Push Notifications Integration Card */}
              <div 
                className="card cursor-pointer hover:shadow-glow transition-all duration-200 overflow-hidden"
                style={{ borderTop: "4px solid #4CAF50" }}
                onClick={() => {}}
              >
                <div className="p-5 flex flex-col h-full">
                  <div className="flex justify-between items-start mb-4">
                    <div style={{ color: "#4CAF50" }}>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                      </svg>
                    </div>
                    <span className="px-2 py-1 text-xs font-medium rounded-full border border-gray-500 text-gray-400">
                      Inactive
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold mb-2">
                    Push Notifications
                  </h3>
                  <p className="text-gray-400 text-sm mb-4 flex-grow">
                    Send notifications to customers about their orders
                  </p>
                  <div className="flex justify-end items-center text-primary-500">
                    <span className="text-sm">Set Up</span>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-1" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Integration details section - would toggle based on which integration was selected */}
          <div className="card">
            <div className="p-6">
              <h2 className="text-lg font-semibold mb-2">
                Twilio Integration
              </h2>
              <p className="text-gray-400 mb-4">
                Connect your Twilio account to enable voice ordering and SMS notifications for your customers.
              </p>
              
              {/* Status alert */}
              <div className="flex items-center p-4 mb-6 text-green-100 bg-green-800 rounded-md">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="ml-2">Twilio integration is active and working properly.</span>
              </div>
              
              <div className="mb-6">
                <h3 className="text-base font-medium mb-3">
                  API Credentials
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label" htmlFor="accountSid">Account SID</label>
                    <input
                      id="accountSid"
                      type="text"
                      className="form-input"
                      name="accountSid"
                      defaultValue="AC1234567890abcdef1234567890abcdef"
                    />
                  </div>
                  <div>
                    <label className="form-label" htmlFor="authToken">Auth Token</label>
                    <input
                      id="authToken"
                      type="password"
                      className="form-input"
                      name="authToken"
                      defaultValue="••••••••••••••••••••••••••••••••"
                    />
                  </div>
                </div>
              </div>
              
              <div className="mb-6">
                <h3 className="text-base font-medium mb-3">
                  Features
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        className="sr-only peer"
                        name="voiceEnabled"
                        defaultChecked={true}
                      />
                      <div className="relative w-11 h-6 bg-dark-500 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                      <span className="ml-3 text-sm font-medium text-gray-300">
                        Voice Ordering
                      </span>
                    </label>
                  </div>
                  <div>
                    <label className="inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        className="sr-only peer"
                        name="smsEnabled"
                        defaultChecked={true}
                      />
                      <div className="relative w-11 h-6 bg-dark-500 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                      <span className="ml-3 text-sm font-medium text-gray-300">
                        SMS Notifications
                      </span>
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="border-t border-dark-600 my-6 pt-6"></div>
              
              <div className="flex justify-end space-x-3">
                <button className="btn-secondary">
                  Cancel
                </button>
                <button className="btn-primary">
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Menu Management Tab */}
      {tabValue === 6 && (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">
              Menu Management
            </h2>
            
            <div className="flex space-x-3">
              {menuActiveTab === 0 && (
                <button
                  className="btn-primary"
                  onClick={handleItemDialogOpen}
                >
                  <AddIcon />
                  <span className="ml-2">Add Item</span>
                </button>
              )}
              
              {menuActiveTab === 1 && (
                <button
                  className="btn-primary"
                  onClick={handleCategoryDialogOpen}
                >
                  <AddIcon />
                  <span className="ml-2">Add Category</span>
                </button>
              )}
            </div>
          </div>
          
          {/* Menu Tab Navigation */}
          <div className="border-b border-dark-600 mb-6">
            <div className="flex">
              <button
                className={`px-4 py-2 font-medium text-sm focus:outline-none ${
                  menuActiveTab === 0 
                    ? 'text-primary-500 border-b-2 border-primary-500' 
                    : 'text-gray-400 hover:text-gray-300'
                }`}
                onClick={() => handleMenuTabChange(0)}
              >
                Menu Items
              </button>
              <button
                className={`px-4 py-2 font-medium text-sm focus:outline-none ${
                  menuActiveTab === 1 
                    ? 'text-primary-500 border-b-2 border-primary-500' 
                    : 'text-gray-400 hover:text-gray-300'
                }`}
                onClick={() => handleMenuTabChange(1)}
              >
                Categories
              </button>
            </div>
          </div>
          
          {/* Search and Filter Bar */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
            <div className="relative w-full sm:w-64">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SearchIcon />
              </div>
              <input
                type="text"
                className="form-input pl-10 w-full"
                placeholder="Search menu items..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>
            
            {menuActiveTab === 0 && (
              <div className="relative">
                <button
                  className="btn-secondary flex items-center"
                  onClick={toggleFilterMenu}
                >
                  <FilterIcon />
                  <span className="ml-2">Filter by Category</span>
                </button>
                
                {filterMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-dark-800 ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
                    <div className="py-1">
                      <button
                        className={`px-4 py-2 text-sm w-full text-left ${
                          selectedCategory === 'all' ? 'text-primary-500' : 'text-gray-300'
                        }`}
                        onClick={() => handleCategorySelect('all')}
                      >
                        All Categories
                      </button>
                      
                      {categories.map(category => (
                        <button
                          key={category.id}
                          className={`px-4 py-2 text-sm w-full text-left ${
                            selectedCategory === category.id ? 'text-primary-500' : 'text-gray-300'
                          }`}
                          onClick={() => handleCategorySelect(category.id)}
                        >
                          {category.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Loading State */}
          {menuLoading ? (
            <div className="flex justify-center items-center h-48">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
            </div>
          ) : (
            <>
              {/* Menu Items Tab */}
              {menuActiveTab === 0 && (
                <div>
                  {menuItems.length === 0 ? (
                    <div className="text-center py-12 bg-dark-800 rounded-md">
                      <div className="mb-4">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                      </div>
                      <h3 className="text-xl font-medium mb-2">No menu items found</h3>
                      <p className="text-gray-400 mb-4">Get started by adding your first menu item.</p>
                      <button 
                        className="btn-primary inline-flex items-center"
                        onClick={handleItemDialogOpen}
                      >
                        <AddIcon />
                        <span className="ml-2">Add Item</span>
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {menuItems
                        .filter(item => {
                          // Filter by search term
                          const matchesSearch = searchTerm === '' || 
                            item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            item.description?.toLowerCase().includes(searchTerm.toLowerCase());
                          
                          // Filter by selected category
                          const matchesCategory = selectedCategory === 'all' || item.categoryId === selectedCategory;
                          
                          return matchesSearch && matchesCategory;
                        })
                        .map(item => (
                          <div key={item.id} className={`card relative ${!item.available ? 'opacity-60' : ''}`}>
                            <div className="p-4">
                              {/* Item Image Placeholder */}
                              <div className="bg-dark-700 h-40 rounded-md flex items-center justify-center mb-4 overflow-hidden">
                                {item.imageUrl ? (
                                  <img src={item.imageUrl} alt={item.name} className="w-full h-full object-cover" />
                                ) : (
                                  <ImageIcon className="text-gray-500 h-12 w-12" />
                                )}
                              </div>
                              
                              <div className="flex justify-between items-start mb-2">
                                <h3 className="text-lg font-medium">{item.name}</h3>
                                <p className="font-semibold">{formatCurrency(item.price)}</p>
                              </div>
                              
                              <p className="text-gray-400 text-sm mb-2 line-clamp-2">{item.description || 'No description available'}</p>
                              
                              <div className="flex justify-between items-center">
                                <span className="px-2 py-1 text-xs font-medium rounded-full bg-dark-600 text-gray-300">
                                  {getCategoryName(item.categoryId)}
                                </span>
                                
                                <label className="inline-flex items-center cursor-pointer">
                                  <span className="mr-2 text-sm text-gray-400">Available</span>
                                  <div className="relative">
                                    <input 
                                      type="checkbox" 
                                      className="sr-only peer"
                                      checked={item.available}
                                      onChange={() => toggleItemAvailability(item.id)}
                                    />
                                    <div className="w-11 h-6 bg-dark-500 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                                  </div>
                                </label>
                              </div>
                            </div>
                            
                            <div className="border-t border-dark-600 p-4 flex justify-end space-x-2">
                              <button className="p-2 hover:bg-dark-600 rounded-full">
                                <EditIcon />
                              </button>
                              <button 
                                className="p-2 hover:bg-dark-600 rounded-full text-red-500"
                                onClick={() => handleDeleteMenuItem(item.id)}
                              >
                                <DeleteIcon />
                              </button>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* Categories Tab */}
              {menuActiveTab === 1 && (
                <div>
                  {categories.length === 0 ? (
                    <div className="text-center py-12 bg-dark-800 rounded-md">
                      <div className="mb-4">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h7" />
                        </svg>
                      </div>
                      <h3 className="text-xl font-medium mb-2">No categories found</h3>
                      <p className="text-gray-400 mb-4">Get started by adding your first category.</p>
                      <button 
                        className="btn-primary inline-flex items-center"
                        onClick={handleCategoryDialogOpen}
                      >
                        <AddIcon />
                        <span className="ml-2">Add Category</span>
                      </button>
                    </div>
                  ) : (
                    <div className="card">
                      <ul className="divide-y divide-dark-600">
                        {categories
                          .filter(category => 
                            searchTerm === '' || 
                            category.name.toLowerCase().includes(searchTerm.toLowerCase())
                          )
                          .map(category => (
                            <li key={category.id} className="p-4 flex justify-between items-center">
                              <div className="flex items-center">
                                <DragIndicatorIcon className="text-gray-500 mr-3" />
                                <span>{category.name}</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button className="p-2 hover:bg-dark-600 rounded-full">
                                  <EditIcon />
                                </button>
                                <button 
                                  className="p-2 hover:bg-dark-600 rounded-full text-red-500"
                                  onClick={() => handleDeleteCategory(category.id)}
                                >
                                  <DeleteIcon />
                                </button>
                              </div>
                            </li>
                          ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          
          {/* Add/Edit Item Dialog */}
          {openItemDialog && (
            <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
              <div className="bg-dark-800 rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-semibold">Add Menu Item</h3>
                    <button 
                      className="text-gray-400 hover:text-white"
                      onClick={handleItemDialogClose}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  
                  <form onSubmit={(e) => { e.preventDefault(); handleItemSubmit(); }}>
                    <div className="mb-4">
                      <label className="form-label" htmlFor="name">Name</label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        className="form-input"
                        value={newItem.name}
                        onChange={handleNewItemChange}
                        required
                      />
                    </div>
                    
                    <div className="mb-4">
                      <label className="form-label" htmlFor="description">Description</label>
                      <textarea
                        id="description"
                        name="description"
                        className="form-input min-h-[100px]"
                        value={newItem.description}
                        onChange={handleNewItemChange}
                      ></textarea>
                    </div>
                    
                    <div className="mb-4">
                      <label className="form-label" htmlFor="price">Price</label>
                      <input
                        id="price"
                        name="price"
                        type="number"
                        step="0.01"
                        min="0"
                        className="form-input"
                        value={newItem.price}
                        onChange={handleNewItemChange}
                        required
                      />
                    </div>
                    
                    <div className="mb-4">
                      <label className="form-label" htmlFor="categoryId">Category</label>
                      <select
                        id="categoryId"
                        name="categoryId"
                        className="form-input"
                        value={newItem.categoryId}
                        onChange={handleNewItemChange}
                        required
                      >
                        <option value="">Select a category</option>
                        {categories.map(category => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="mb-4">
                      <label className="form-label" htmlFor="tags">Tags (comma separated)</label>
                      <input
                        id="tags"
                        name="tags"
                        type="text"
                        className="form-input"
                        placeholder="e.g. vegetarian, spicy, gluten-free"
                        value={Array.isArray(newItem.tags) ? newItem.tags.join(', ') : ''}
                        onChange={(e) => {
                          const tagsString = e.target.value;
                          const tagsArray = tagsString.split(',').map(tag => tag.trim()).filter(tag => tag);
                          setNewItem(prev => ({
                            ...prev,
                            tags: tagsArray
                          }));
                        }}
                      />
                    </div>

                    <div className="mb-6">
                      <label className="inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          className="sr-only peer"
                          name="available"
                          checked={newItem.available}
                          onChange={handleNewItemChange}
                        />
                        <div className="relative w-11 h-6 bg-dark-500 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                        <span className="ml-3 text-sm font-medium text-gray-300">
                          Available
                        </span>
                      </label>
                    </div>
                    
                    <div className="flex justify-end space-x-3">
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={handleItemDialogClose}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn-primary"
                      >
                        Save Item
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}
          
          {/* Add/Edit Category Dialog */}
          {openCategoryDialog && (
            <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
              <div className="bg-dark-800 rounded-lg shadow-xl max-w-md w-full">
                <div className="p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-semibold">Add Category</h3>
                    <button 
                      className="text-gray-400 hover:text-white"
                      onClick={handleCategoryDialogClose}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  
                  <form onSubmit={(e) => { e.preventDefault(); handleCategorySubmit(); }}>
                    <div className="mb-6">
                      <label className="form-label" htmlFor="categoryName">Category Name</label>
                      <input
                        id="categoryName"
                        name="name"
                        type="text"
                        className="form-input"
                        value={newCategory.name}
                        onChange={handleNewCategoryChange}
                        required
                      />
                    </div>
                    
                    <div className="flex justify-end space-x-3">
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={handleCategoryDialogClose}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn-primary"
                      >
                        Save Category
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Users & Permissions Tab */}
      {tabValue === 7 && (
        <div>
          <UsersPage />
        </div>
      )}
      
      {/* Settings Tab */}
      {tabValue === 5 && (
        <div>
          <SettingsPage />
        </div>
      )}

      {/* Status Change Confirmation Modal */}
      {statusModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
          <div className="bg-dark-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-start mb-4">
                <div className={`rounded-full p-2 ${business.status === 'active' ? 'bg-red-900 bg-opacity-20 text-red-500' : 'bg-green-900 bg-opacity-20 text-green-500'} mr-4`}>
                  {business.status === 'active' ? <DeleteIcon className="h-6 w-6" /> : <CheckCircleIcon className="h-6 w-6" />}
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">
                    {business.status === 'active' ? 'Deactivate Business' : 'Activate Business'}
                  </h3>
                  <p className="text-gray-400">
                    {business.status === 'active'
                      ? `Are you sure you want to deactivate ${business.name}? The business will no longer be accessible to customers.`
                      : `Are you sure you want to activate ${business.name}? The business will be accessible to customers.`}
                  </p>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setStatusModalOpen(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={toggleBusinessStatus}
                  className={`px-4 py-2 rounded font-medium ${
                    business.status === 'active'
                      ? 'bg-red-700 hover:bg-red-600 text-white'
                      : 'bg-green-700 hover:bg-green-600 text-white'
                  }`}
                >
                  {business.status === 'active' ? 'Deactivate' : 'Activate'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessDetailPage;